import {Component, DestroyRef, inject, OnInit, ViewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import {
    filter,
    map,
    switchMap,
    take,
} from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';
import {TreeNode, TreeModel, KEYS} from '@ali-hm/angular-tree-component';
import {
    AddChildLocationDialogComponent
} from '@emm/shared/dialogs/add-child-location-dialog/add-child-location-dialog.component';
import {locationSetsFilterFieldMap as fieldMap} from '@emm/shared/formly/maps/location-sets-filter.config';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {LocationSetsFormComponent} from '@emm/management/components/location-sets-form/location-sets-form.component';
import {GenericItemsTreeComponent} from '@emm/management/components/generic-items-tree/generic-items-tree.component';
import {LocationSetType} from '@emm/core/models/location-set-type.model';
import {TreeUtil} from '@emm/core/util/angular-tree-component.util';
import {ContextMenuAction} from '@emm/core/models/context-menu-action.model';
import {LocationSet} from '@emm/core/models/location-set.model';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {DialogService} from '@emm/shared/services/dialog.service';
import {LocationSetsFacadeService} from '@emm/management/services/location-sets-facade.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {
    AddLocationSetDialogComponent
} from '@emm/shared/dialogs/add-location-set-dialog/add-location-set-dialog.component';
import {SearchFilter} from '@emm/core/models/search-filter';
import {submitFormWithDetail} from '@emm/core/util/rx.util';
import {UserService} from '@emm/core/services/user.service';
import {FlagKeys, LaunchDarklyService} from '@emm/core/services/launch-darkly.service';
import {API_ERRORS} from '@emm/core/util/api-constants';
import {
    RemoveChildLocationDialogComponent
} from '@emm/shared/dialogs/remove-child-location-dialog/remove-child-location-dialog.component';

@Component({
    selector: 'emm-location-sets',
    styleUrls: ['./location-sets.component.scss'],
    templateUrl: './location-sets.component.html',
    standalone: false
})
export class LocationSetsComponent implements OnInit {

    @ViewChild(LocationSetsFormComponent) locationSetsForm: LocationSetsFormComponent;
    @ViewChild(GenericItemsTreeComponent) tree: GenericItemsTreeComponent;

    readonly cannotBeEditedText = 'This location set is imported and cannot be edited.';

    anyLoading$ = this.locationSetsFacade.anyLoading$;
    centerLoading$ = this.locationSetsFacade.centerLoading$;
    isReadOnly$ = this.authFacade.isReadOnly$;
    isReadOnly = this.userService.hasLoginRole();
    isSuperAdmin = this.userService.hasSuperAdminRole();
    isAdmin = this.userService.hasAdminRole();
    isAuthor = this.userService.hasAuthorRole();
    isMMSAuthor = this.userService.isMmsAuthorOnly();
    locationSetDetail$ = this.locationSetsFacade.locationSetDetail$;
    rightLoading$ = this.locationSetsFacade.rightLoading$;
    selectedLocationSetType: LocationSetType;
    allLocations$ = this.locationSetsFacade.allLocations$;

    fields$ = this.locationSetsFacade.locationSetTypes$.pipe(map(fieldMap));
    nodes$ = this.locationSetsFacade.locationSetsAsNodes$;

    locationSetTypes$ = this.locationSetsFacade.locationSetTypes$;
    disableLocationSetsToggle$ = this.launchDarklyService.getBooleanFlag(FlagKeys.DISABLE_LOCATION_SETS);

    options = {
        getChildren: (node: TreeNode) =>
            this.locationSetsFacade.getLocationsByParent(node.id).toPromise(),
        actionMapping: {
            mouse: {
                click: (tree: TreeModel, node: TreeNode) => {
                    if (node?.data?.data) {
                        this.selectLocationSet(node);
                    }
                }
            },
            keys: {
                [KEYS.ENTER]: (tree, node) => alert(`This is ${node.data.name}`)
            }
        }
    };

    locationSetsContextMenuActions$ = this.disableLocationSetsToggle$.pipe(
        map(toggle => [
            {
                // Add
                enabled: () => !this.selectedLocationSetType.locked,
                click: (node) => this.addChildLocations(node),
                visible: (node) => (this.isSuperAdmin || this.isAdmin) ? TreeUtil.isRoot(node) : false,
                icon: R.always('add'),
                text: R.always('Add Location'),
                tooltip: () => (this.selectedLocationSetType.locked ? this.cannotBeEditedText : '')
            },
            {
                // Bulk Remove Locations
                enabled: () => !this.selectedLocationSetType.locked,
                click: (node) => this.removeChildLocations(node),
                visible: (node) => (this.isSuperAdmin) ? TreeUtil.isRoot(node) : false,
                icon: R.always('delete'),
                text: R.always('Remove Locations'),
                tooltip: () => (this.selectedLocationSetType.locked ? this.cannotBeEditedText : '')
            },
            {
                // Delete
                enabled: () => !this.selectedLocationSetType.locked,
                click: (node) =>
                    TreeUtil.isRoot(node) ? this.deleteLocationRoot(node) : this.deleteLocation(node),
                visible: (node) => (this.isSuperAdmin),
                icon: R.always('delete'),
                text: (node) => (TreeUtil.isRoot(node) ? 'Delete' : 'Remove'),
                tooltip: () => (this.selectedLocationSetType.locked ? this.cannotBeEditedText : '')
            },
            {
                // Disable/Enable
                enabled: () => R.T,
                click: (node) => this.toggleLocationDisabled(node),
                visible: (node) => this.isSuperAdmin && toggle && node.data.data.scope === 'Custom',
                icon: (node) => node.data.data.disabled ? 'visibility_on' : 'visibility_off',
                text: (node) => node.data.data.disabled ? 'Enable' : 'Disable',
                tooltip: () => (this.selectedLocationSetType.locked ? this.cannotBeEditedText : '')
            },
            {
                // Download as CSV
                enabled: () => R.T,
                click: (node) => this.downloadLocationSetAsCSV(node),
                visible: (node) => R.T,
                icon: (node) => 'download',
                text: (node) => 'Download',
                tooltip: () => ' Download location set as CSV',
            }
        ] as ContextMenuAction<TreeNode>[])
    );

    prevLocationSet: LocationSet;
    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    constructor(
        private authFacade: AuthFacadeService,
        private dialog: MatDialog,
        private dialogService: DialogService,
        private locationSetsFacade: LocationSetsFacadeService,
        private notificationService: NotificationService,
        private userService: UserService,
        private launchDarklyService: LaunchDarklyService
    ) {
    }


    ngOnInit(): void {
        this.locationSetsFacade.init();
        this.anyLoading$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

        this.locationSetsFacade.selectedLocationSetType$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(value => this.selectedLocationSetType = value);
    }


    openCreateLocationSetDialog() {
        let locationSetTypes;
        this.locationSetTypes$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(res => {
            locationSetTypes = res;
        });
        const dialogRef = this.dialog.open(AddLocationSetDialogComponent, {
            width: '600px',
            data: {showCreateLocationSet: true, locationSetTypes, closeOnNavigation: true},
        });
    }


    onFilter(searchFilter: SearchFilter) {
        this.locationSetsFacade.selectLocationSetType(searchFilter.value as string);
    }


    selectLocationSet(node: TreeNode) {
        const selected: LocationSet = node.data.data;
        if (RA.isNotNil(selected)) {
            this.checkIfItemHasChanges(this.prevLocationSet)
                .pipe(take(1), takeUntilDestroyed(this.destroyRef))
                .subscribe((_) => {
                    this.prevLocationSet = selected;
                    this.locationSetsFacade.selectLocationSet(selected);
                    node.setIsActive(true);
                });
        } else {
            this.prevLocationSet = null;
        }
    }


    checkIfItemHasChanges(prevLocationSet: LocationSet): Observable<boolean> {
        return this.isReadOnly$.pipe(
            take(1),
            switchMap((isReadOnly) => {
                if (this.hasChanges() && !isReadOnly && RA.isNotNil(prevLocationSet)) {
                    return this.dialogService
                        .openSaveChangesDialog(prevLocationSet.name)
                        .afterClosed()
                        .pipe(
                            switchMap((res) => {
                                if (res) {
                                    this.locationSetsForm.submit(prevLocationSet.tag);
                                    return of(false);
                                } else {
                                    this.locationSetsForm.cancel();
                                    return of(true);
                                }
                            })
                        );
                }
                return of(true);
            })
        );
    }


    hasChanges(): boolean {
        return this.locationSetsForm && this.locationSetsForm.hasChanges();
    }


    addChildLocations(node: TreeNode): void {
        const location = node.data.data;
        const dialogRef = this.dialog.open(AddChildLocationDialogComponent, {
            width: '600px',
            data: {
                allLocations: this.allLocations$.value,
            },
        });
        dialogRef
            .afterClosed()
            .pipe(
                filter((newLocations) => !!newLocations),
                switchMap((newLocations) =>
                    this.locationSetsFacade.api
                        .addChildLocations(location.tag, newLocations)),
                takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (result) => {
                    this.notificationService.success(`Successfully added location(s) to ${location.name}`);
                    this.locationSetsFacade.reloadLocationSets(location.scope.toUpperCase());
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                }
            });
    }

    removeChildLocations(node: TreeNode): void {
        const location = node.data.data;
        const dialogRef = this.dialog.open(RemoveChildLocationDialogComponent, {
            width: '600px',
            data: {
                allLocations: this.allLocations$.value,
            },
        });
        dialogRef
            .afterClosed()
            .pipe(
                filter((newLocations) => !!newLocations),
                switchMap((newLocations) =>
                    this.locationSetsFacade.api
                        .removeChildLocations(location.tag, newLocations)),
                takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (result) => {
                    this.notificationService.success(`Successfully added location(s) to ${location.name}`);
                    this.locationSetsFacade.reloadLocationSets(location.scope.toUpperCase());
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                }
            });
    }


    deleteLocationRoot(node: TreeNode): void {
        const location = node.data.data;
        this.dialogService
            .openConfirmDialog('Are you sure you want to delete', location.name)
            .afterClosed()
            .pipe(
                filter((res) => !!res),
                switchMap(() =>
                    this.locationSetsFacade.api.deleteLocationSet(location.tag)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(
                {
                    next: (result) => {
                        this.notificationService.success(`Successfully deleted ${location.name}`);
                        this.locationSetsFacade.reloadLocationSets(location.scope.toUpperCase());
                    },
                    error: (error) => {
                        this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                    }
                });
    }


    deleteLocation(node: TreeNode): void {
        const location = node.data.data;
        const parent = node.parent.data.data;
        const expandParentNode = node.parent.expand();
        this.dialogService
            .openConfirmDialog('Are you sure you want to remove', location.name)
            .afterClosed()
            .pipe(
                filter((res) => !!res),
                switchMap(() =>
                    this.locationSetsFacade.api.removeChildLocation(parent.tag, location.tag)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                    next: () => {
                        this.notificationService.success(`Successfully deleted ${location.name} from ${parent.name}`);
                        this.locationSetsFacade.reloadLocationSets(parent.scope.toUpperCase());
                    },
                    error: (error) => {
                        this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                    }
                }
            );
    }

    toggleLocationDisabled(node) {
        const location = node.data.data;
        this.locationSetsFacade.api.disableLocationSet(location.tag, !location.disabled).pipe(take(1))
            .subscribe({
                    next: () => {
                        this.notificationService.success(`Successfully toggled ${location.name} to `);
                        this.locationSetsFacade.reloadLocationSets(location.scope.toUpperCase());
                    }, error: (error) => {
                        this.notificationService.error(`Failed to toggle ${location.name} to `);
                    }
                }
            );
    }

    downloadLocationSetAsCSV(node: TreeNode) {
        this.locationSetsFacade.getLocationsByParent(node.id).pipe(take(1)).subscribe(locations => {
            if (!locations.length || locations.length === 0) {
                this.notificationService.error('No data to convert to CSV.');
                return;
            }

            const headers = ['Store Number, Store Name'];
            const csvRows = [
                headers, // header row
                ...locations.map(location => [location?.data?.tag, location?.data?.name])
            ];

            const csvContent = csvRows.join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob);

            const downloadLink = document.createElement('a');
            downloadLink.href = url;
            downloadLink.setAttribute('download', `${node.data.name} Locations.csv`);
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            URL.revokeObjectURL(url);
            this.notificationService.success(`Successfully downloaded ${node.data.name} Locations.csv`);
        });
    }

    submitForm(): void {
        submitFormWithDetail(this.locationSetDetail$, this.locationSetsForm)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
    }
}
